import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BrandIcon from '../components/BrandIcon';
import { Spinner } from '../components/ui';
import { theme } from '../styles/theme';

const API_URL = process.env.REACT_APP_API_URL;

export default function PublicAgentPage() {
  const { userSlug, agentSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const [agent, setAgent]       = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [sessionId, setSessionId]   = useState(null);
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [sending, setSending]       = useState(false);
  const [msgCount, setMsgCount]     = useState(0);
  const [limitHit, setLimitHit]     = useState(false);
  const pollRef  = useRef(null);
  const endRef   = useRef(null);

  const MSG_LIMIT = 15;

  // Carrega dados do agente
  useEffect(() => {
    fetch(`${API_URL}/public/agent-page/${userSlug}/${agentSlug}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setAgent(data);
        setTenantId(data.tenantId);
      })
      .catch(() => setNotFound(true));
  }, [userSlug, agentSlug]);

  // Inicia sessão assim que tiver tenantId
  useEffect(() => {
    if (!tenantId || !agent) return;
    fetch(`${API_URL}/webchat/${tenantId}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentInstanceId: agent.instanceId })
    })
      .then(r => r.json())
      .then(d => {
        setSessionId(d.sessionId);
        const greeting = agent.welcomeMessage || t('publicAgent.greeting', { name: agent.name, defaultValue: `Olá! Sou ${agent.name}. Como posso te ajudar?` });
        setMessages([{ from: 'agent', text: greeting, time: fmtTime() }]);
      })
      .catch(() => {});
  }, [tenantId, agent]); // eslint-disable-line

  // Polling de respostas
  useEffect(() => {
    if (!sessionId || !tenantId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r  = await fetch(`${API_URL}/webchat/${tenantId}/poll?sessionId=${sessionId}`);
        const d  = await r.json();
        if (d.response) {
          setMessages(prev => [...prev, { from: 'agent', text: d.response, time: fmtTime(d.timestamp) }]);
        }
      } catch { /* silencioso */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [sessionId, tenantId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || sending || limitHit || !sessionId) return;
    if (msgCount >= MSG_LIMIT) { setLimitHit(true); return; }
    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { from: 'me', text, time: fmtTime() }]);
    setSending(true);
    setMsgCount(c => c + 1);
    try {
      await fetch(`${API_URL}/webchat/${tenantId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, message: text, agentInstanceId: agent.instanceId })
      });
    } catch { /* silencioso */ }
    finally { setSending(false); }
  };

  if (notFound) return <NotFoundPage navigate={navigate} t={t} />;
  if (!agent)   return <LoadingPage />;

  const color = agent.primaryColor || '#6366f1';

  return (
    <Page>
      <Header $color={color}>
        <AgentAvatar><BrandIcon icon={agent.icon || '🤖'} size={28} /></AgentAvatar>
        <HeaderInfo>
          <AgentName>{agent.name}</AgentName>
          {agent.role && <AgentRole>{agent.role}</AgentRole>}
        </HeaderInfo>
        <OnlineDot />
      </Header>

      <ChatArea>
        {messages.map((msg, i) => (
          <MsgRow key={i} $from={msg.from}>
            <Bubble $from={msg.from} $color={color}>{msg.text}</Bubble>
            <Time>{msg.time}</Time>
          </MsgRow>
        ))}
        {limitHit && (
          <LimitBanner>
            <span>{t('publicAgent.limitReached', { defaultValue: 'Gostou? Crie seu agente grátis e sem limites.' })}</span>
            <CtaBtn $color={color} onClick={() => navigate('/welcome')}>
              {t('publicAgent.createFree', { defaultValue: 'Criar grátis →' })}
            </CtaBtn>
          </LimitBanner>
        )}
        <div ref={endRef} />
      </ChatArea>

      <InputRow>
        <ChatInput
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder={limitHit
            ? t('publicAgent.limitPlaceholder', { defaultValue: 'Limite atingido' })
            : t('publicAgent.placeholder', { defaultValue: 'Digite uma mensagem...' })}
          disabled={sending || limitHit || !sessionId}
          enterKeyHint="send"
        />
        <SendBtn aria-label="Enviar" $color={color} onClick={send} disabled={sending || limitHit || !input.trim() || !sessionId}>
          {sending ? <Spinner size="14px" /> : <Send size={14} />}
        </SendBtn>
      </InputRow>

      <PoweredBy>
        {t('publicAgent.poweredBy', { defaultValue: 'Criado com' })}{' '}
        <a href="https://libbenagents.com" target="_blank" rel="noreferrer">Libben Agents</a>
        {' · '}
        <a href="/welcome">{t('publicAgent.createYours', { defaultValue: 'Crie o seu grátis' })}</a>
      </PoweredBy>
    </Page>
  );
}

function LoadingPage() {
  return (
    <Page style={{ alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size="32px" />
    </Page>
  );
}

function NotFoundPage({ navigate, t }) {
  return (
    <Page style={{ alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
      <div style={{ fontSize: 48 }}>🤖</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: theme.colors.textPrimary }}>
        {t('publicAgent.notFound', { defaultValue: 'Agente não encontrado' })}
      </div>
      <div style={{ fontSize: 14, color: theme.colors.textMuted, textAlign: 'center' }}>
        {t('publicAgent.notFoundSub', { defaultValue: 'Este link pode ter expirado ou o agente foi removido.' })}
      </div>
      <CtaBtn $color={theme.colors.primary} onClick={() => navigate('/welcome')}>
        {t('publicAgent.createFree', { defaultValue: 'Criar meu agente grátis →' })}
      </CtaBtn>
    </Page>
  );
}

function fmtTime(iso) {
  return new Date(iso || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Styled ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  display: flex; flex-direction: column;
  height: 100dvh; max-width: 480px; margin: 0 auto;
  background: #f9fafb; font-family: system-ui, sans-serif;
  padding-top: env(safe-area-inset-top);
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: ${p => p.$color};
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
`;

const AgentAvatar = styled.div`font-size: 28px; line-height: 1;`;
const HeaderInfo  = styled.div`flex: 1;`;
const AgentName   = styled.div`font-size: 16px; font-weight: 700;`;
const AgentRole   = styled.div`font-size: 12px; opacity: 0.85;`;
const OnlineDot   = styled.div`
  width: 10px; height: 10px; border-radius: 50%;
  background: #4ade80; border: 2px solid rgba(255,255,255,0.6);
`;

const ChatArea = styled.div`
  flex: 1; overflow-y: auto; padding: 16px 12px;
  display: flex; flex-direction: column; gap: 8px;
`;

const MsgRow = styled.div`
  display: flex; flex-direction: column;
  align-items: ${p => p.$from === 'me' ? 'flex-end' : 'flex-start'};
`;

const Bubble = styled.div`
  max-width: 78%; padding: 10px 14px;
  border-radius: ${p => p.$from === 'me' ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};
  background: ${p => p.$from === 'me' ? p.$color : 'white'};
  color: ${p => p.$from === 'me' ? 'white' : theme.colors.textPrimary};
  font-size: 14px; line-height: 1.5;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  white-space: pre-wrap;
`;

const Time = styled.div`font-size: 10px; color: ${theme.colors.gray400}; margin-top: 2px;`;

const LimitBanner = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 16px; margin: 8px 0;
  background: ${theme.colors.warningLight}; border: 1px solid #fde68a;
  border-radius: 12px; text-align: center;
  font-size: 14px; color: #92400e;
`;

const InputRow = styled.div`
  display: flex; gap: 8px; padding: 10px 12px;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  background: white; border-top: 1px solid #e5e7eb;
`;

const ChatInput = styled.input`
  flex: 1; padding: 10px 14px;
  border: 1px solid #e5e7eb; border-radius: 24px;
  font-size: 14px; font-family: inherit; outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
  &:disabled { background: #f3f4f6; color: ${theme.colors.gray400}; }
`;

const SendBtn = styled.button`
  width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$color}; color: white; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CtaBtn = styled.button`
  padding: 10px 20px;
  background: ${p => p.$color}; color: white; border: none;
  border-radius: 24px; font-size: 14px; font-weight: 600;
  cursor: pointer;
`;

const PoweredBy = styled.div`
  text-align: center; padding: 8px; font-size: 11px; color: ${theme.colors.gray400};
  background: white; border-top: 1px solid #f3f4f6;
  a { color: ${theme.colors.primary}; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;
